import { tenantTypeSelectData } from '@lib/tenantTypes'
import { isE2eFixtureTenant } from '@lib/e2eFixtureTenant'
import { useTenants } from '@lib/tenantsContext'
import {
  Alert,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput
} from '@mantine/core'
import { useInputState } from '@mantine/hooks'
import { usePersistentState } from '@packages/core'
import { H1, Button, SortableTable } from '@packages/ui'
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconFileText,
  IconPlayerPlay,
  IconPlus,
  IconLock,
  IconRefresh,
  IconTrash
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSupabase } from '@packages/supabase'
import { useTranslation } from 'react-i18next'

export const Dashboard = () => {
  const { t } = useTranslation()
  const tenants = useTenants()
  const { supabase } = useSupabase()
  const navigate = useNavigate()
  const [filterBy, setFilterBy] = usePersistentState<
    'all' | 'new' | 'active' | 'expired' | 'blocked' | boolean
  >('admin-filter-tenant-by', 'new')
  const [hideE2eTenants, setHideE2eTenants] = usePersistentState(
    'admin-hide-e2e-tenants',
    true
  )

  const data = useMemo(() => {
    if (!tenants.data) {
      return []
    }
    let rows = tenants.data
    if (hideE2eTenants) {
      rows = rows.filter((tenant) => !isE2eFixtureTenant(tenant))
    }
    if (!filterBy || filterBy === 'all') return rows

    switch (filterBy) {
      case 'active':
        return rows.filter(
          (e: Tenant) =>
            !e.demo_expiry_date || dayjs(e.demo_expiry_date).isAfter()
        )
      case 'expired':
        return rows.filter((e: Tenant) => dayjs(e.demo_expiry_date).isBefore())
      case 'blocked':
        return rows.filter((e: Tenant) => e.suspended_at !== null)
      case 'new':
        return rows.filter((e) =>
          dayjs(e.created_at).isAfter(dayjs().subtract(5, 'days'))
        )
    }
  }, [filterBy, hideE2eTenants, tenants.data])

  const selectTenant = (tenant: Tenant) => {
    navigate(`/tenants/${tenant.id}`, { state: { tenant } })
  }

  const [filterFor, setFilterFor] = useInputState<string>('')
  const [createTenantOpened, setCreateTenantOpened] = useState(false)
  const [createTenantName, setCreateTenantName] = useState('')
  const [createTenantType, setCreateTenantType] = useState<string>('')
  const [createTenantLoading, setCreateTenantLoading] = useState(false)
  const [createTenantError, setCreateTenantError] = useState<string | null>(
    null
  )
  const [addCreatorAsMember, setAddCreatorAsMember] = useState(true)

  // Expired-trial sweep: mirrors the daily pg_cron job so operators can
  // preview / trigger the purge without waiting for 03:00 UTC.
  const [sweepOpened, setSweepOpened] = useState(false)
  const [sweepAgeDays, setSweepAgeDays] = useState<number>(30)
  const [sweepDryRun, setSweepDryRun] = useState(true)
  const [sweepRunning, setSweepRunning] = useState(false)
  const [sweepError, setSweepError] = useState<string | null>(null)
  const [sweepResult, setSweepResult] = useState<{
    tenants_considered: number
    tenants_purged: number
    tenants_partial: number
    tenants_failed: number
    tenants_skipped: number
    dry_run: boolean
    results: Array<{
      tenant_id: string
      tenant_name?: string
      status: string
      errors: string[]
    }>
  } | null>(null)

  const runExpiredTrialSweep = async () => {
    setSweepRunning(true)
    setSweepError(null)
    setSweepResult(null)
    try {
      const { data, error } = await supabase.functions.invoke(
        'process-expired-trials',
        {
          body: {
            source: 'admin_console',
            age_days: sweepAgeDays,
            dry_run: sweepDryRun
          }
        }
      )
      if (error) {
        setSweepError(error.message)
      } else {
        setSweepResult(data as typeof sweepResult)
        if (!sweepDryRun) await tenants.mutate()
      }
    } catch (e: any) {
      setSweepError(e?.message ?? String(e))
    } finally {
      setSweepRunning(false)
      setSweepOpened(false)
    }
  }

  const createTenant = async () => {
    if (!createTenantName.trim()) return
    setCreateTenantLoading(true)
    setCreateTenantError(null)

    const { data, error } = await supabase.rpc('admin_create_tenant', {
      p_name: createTenantName.trim(),
      p_type: createTenantType || '',
      p_add_creator_as_member: addCreatorAsMember
    })

    setCreateTenantLoading(false)

    if (error) {
      setCreateTenantError(error.message)
      return
    }

    const createdTenant = Array.isArray(data) ? data[0] : data
    setCreateTenantOpened(false)
    setCreateTenantName('')
    setCreateTenantType('')
    setAddCreatorAsMember(true)
    await tenants.mutate()

    if (createdTenant?.id) {
      navigate(`/tenants/${createdTenant.id}`, {
        state: { tenant: createdTenant }
      })
    }
  }

  return (
    <Stack c={'white'}>
      <H1 className={'text-white'}>Tenants Management</H1>

      <TextInput
        classNames={{
          root: 'group',
          input:
            'w-full rounded-full group-focus:bg-white/40 bg-white/5 text-white'
        }}
        value={filterFor}
        onChange={setFilterFor}
      />

      <Group justify={'space-between'}>
        <Group c={'white'}>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateTenantOpened(true)}
          >
            Create Tenant
          </Button>
          <Button
            variant="transparent"
            active={filterBy === 'new'}
            onClick={() => setFilterBy('new')}
          >
            New
          </Button>
          <Button
            variant="transparent"
            active={filterBy === 'active'}
            onClick={() => setFilterBy('active')}
          >
            Active
          </Button>
          <Button
            variant="transparent"
            active={filterBy === 'expired'}
            onClick={() => setFilterBy('expired')}
          >
            Expired
          </Button>
          {filterBy === 'expired' && (
            <Button
              color="red"
              leftSection={<IconPlayerPlay size={14} />}
              onClick={() => setSweepOpened(true)}
            >
              Run trial sweep…
            </Button>
          )}
          <Button
            variant="transparent"
            active={filterBy === 'blocked'}
            onClick={() => setFilterBy('blocked')}
          >
            Blocked
          </Button>

          <Button
            variant="transparent"
            active={!filterBy || filterBy === 'all'}
            onClick={() => setFilterBy('all')}
          >
            All
          </Button>

          <Checkbox
            classNames={{ label: 'text-white' }}
            checked={hideE2eTenants}
            onChange={(e) => setHideE2eTenants(e.currentTarget.checked)}
            label={t('admin.tenants.hide_e2e_fixtures', {
              defaultValue: 'Hide E2E test tenants'
            })}
          />

          <Button
            c={'white'}
            variant={'transparent'}
            loading={tenants.isLoading || tenants.isValidating}
            loaderProps={{ type: 'dots' }}
            onClick={() => tenants.mutate()}
          >
            <IconRefresh size={20} />
          </Button>
        </Group>

        <Group>
          <Button
            variant="default"
            leftSection={<IconTrash size={16} />}
            onClick={() => navigate('/tenants/pending-deletions')}
          >
            Pending Deletions
          </Button>
          <Button
            variant="default"
            leftSection={<IconFileText size={16} />}
            onClick={() => navigate('/tenants/deletion-logs')}
          >
            Deletion Logs
          </Button>
        </Group>
      </Group>

      {filterBy === 'expired' && (sweepResult || sweepError) && (
        <Alert
          color={
            sweepError || (sweepResult?.tenants_failed ?? 0) > 0
              ? 'red'
              : 'green'
          }
          title={
            sweepError
              ? 'Trial sweep failed'
              : `Trial sweep ${sweepResult?.dry_run ? '(dry run)' : 'complete'}`
          }
          withCloseButton
          onClose={() => {
            setSweepResult(null)
            setSweepError(null)
          }}
        >
          {sweepError ? (
            <Text size="sm">{sweepError}</Text>
          ) : (
            sweepResult && (
              <Stack gap={4}>
                <Text size="sm">
                  Considered: {sweepResult.tenants_considered} · Purged:{' '}
                  {sweepResult.tenants_purged} · Partial:{' '}
                  {sweepResult.tenants_partial} · Failed:{' '}
                  {sweepResult.tenants_failed} · Skipped:{' '}
                  {sweepResult.tenants_skipped}
                </Text>
                {sweepResult.results
                  .filter((r) => r.errors?.length > 0)
                  .slice(0, 5)
                  .map((r) => (
                    <Text key={r.tenant_id} size="xs" c="red">
                      {r.tenant_name ?? r.tenant_id}: {r.errors.join('; ')}
                    </Text>
                  ))}
              </Stack>
            )
          )}
        </Alert>
      )}

      <Stack>
        <SortableTable
          pageSize={25}
          filterBy={filterFor}
          data={data}
          columns={[
            {
              key: 'name',
              label: 'Name'
            },
            {
              key: 'type',
              label: 'Type',
              render: (value) => value || '-'
            },
            {
              key: 'suspended_at',
              label: 'Status',
              render: (_e, tenant) => {
                return (
                  <Group>
                    {tenant.suspended_at && (
                      <Group>
                        <IconLock color={'red'} /> Blocked
                      </Group>
                    )}
                    {tenant.demo_expiry_date && (
                      <>
                        {dayjs(tenant.demo_expiry_date).isBefore(
                          new Date()
                        ) && (
                          <Group>
                            <IconAlertCircle color={'red'} /> Trial Expired
                          </Group>
                        )}
                        {dayjs(tenant.demo_expiry_date).isAfter(
                          dayjs().subtract(5, 'days')
                        ) && (
                          <Group>
                            <IconAlertTriangle color={'orange'} /> Trial ending
                          </Group>
                        )}
                      </>
                    )}
                  </Group>
                )
              }
            },
            { key: 'inactive_users', label: 'Inactive Users', render: () => 0 },
            {
              key: 'active_users',
              label: 'Active Users',
              render: (_v, tenant) => tenant.user_count[0].count
            },
            {
              key: 'created_at',
              label: 'Registered',
              render: (created_at) => dayjs(created_at).format('DD.MM.YYYY')
            }
          ]}
          rowOnClick={selectTenant}
        />
      </Stack>

      <Modal
        opened={createTenantOpened}
        onClose={() => {
          setCreateTenantOpened(false)
          setCreateTenantError(null)
          setAddCreatorAsMember(true)
        }}
        title="Create Tenant"
      >
        <Stack c="black">
          <TextInput
            classNames={{
              label: ' text-white'
            }}
            label="Tenant name"
            placeholder="Tenant name"
            value={createTenantName}
            onChange={(e) => setCreateTenantName(e.currentTarget.value)}
            required
          />
          <Select
            classNames={{
              label: ' text-white'
            }}
            label="Type"
            data={tenantTypeSelectData()}
            value={createTenantType}
            onChange={(value) => setCreateTenantType(value || '')}
            clearable={false}
          />
          <Checkbox
            classNames={{
              label: ' text-white'
            }}
            checked={addCreatorAsMember}
            onChange={(e) => setAddCreatorAsMember(e.currentTarget.checked)}
            label={t('admin.tenants.add_creator_as_member', {
              defaultValue: 'Add me as a member of this tenant'
            })}
            description={t('admin.tenants.add_creator_as_member_hint', {
              defaultValue:
                'Recommended: creates a membership so you can open this tenant in the main app. Turn off only if you will invite another owner or attach yourself later.'
            })}
          />
          {createTenantError && <Text c="red">{createTenantError}</Text>}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setCreateTenantOpened(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={createTenant}
              loading={createTenantLoading}
              disabled={!createTenantName.trim()}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={sweepOpened}
        onClose={() => setSweepOpened(false)}
        title="Run expired-trial sweep"
      >
        <Stack c="black">
          <Text size="sm">
            Invokes <code>process-expired-trials</code> (the same edge
            function that runs daily at 03:00 UTC). Trials older than the
            threshold below <strong>and</strong> without an active Stripe
            subscription will be permanently deleted. This path does not
            honour the 30-day recovery window.
          </Text>
          <NumberInput
            label="Minimum days expired"
            description="Matches the daily cron default of 30"
            min={1}
            max={365}
            value={sweepAgeDays}
            onChange={(v) =>
              setSweepAgeDays(typeof v === 'number' ? v : Number(v) || 30)
            }
          />
          <Switch
            checked={sweepDryRun}
            onChange={(e) => setSweepDryRun(e.currentTarget.checked)}
            label="Dry run (don't actually delete)"
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setSweepOpened(false)}
              disabled={sweepRunning}
            >
              Cancel
            </Button>
            <Button
              color={sweepDryRun ? 'blue' : 'red'}
              leftSection={<IconPlayerPlay size={14} />}
              onClick={runExpiredTrialSweep}
              loading={sweepRunning}
            >
              {sweepDryRun ? 'Dry run' : 'Purge now'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
